import type { IconRegistry } from '../assets/IconRegistry';
import type { Painter } from '../rendering/Painter';
import { overlayTokens } from '../styles/tokens';
import type { ActionBarVM } from '../vm/types';
import type { Rect } from '../../../engine/hex/types';

export type HitTarget = { id: string; rect: Rect; enabled: boolean };

type ActionBarOptions = {
    icons: IconRegistry;
};

export class ActionBar {
    constructor(private options: ActionBarOptions) {}

    render(painter: Painter, vm: ActionBarVM): HitTarget[] {
        const targets: HitTarget[] = [];
        painter.panel(vm.rect, overlayTokens.colors.panelFill, overlayTokens.colors.panelStroke);

        vm.buttons.forEach((button) => {
            targets.push({ id: button.id, rect: button.rect, enabled: button.enabled });
            const colors = resolveButtonColors(button.enabled, button.active);
            painter.button(button.rect, colors.fill, colors.stroke);

            if (button.iconId && button.iconReady) {
                const icon = this.options.icons.get(button.iconId as 'settings');
                if (icon) {
                    const size = Math.min(button.rect.w, button.rect.h) - 6;
                    painter.image(icon, {
                        x: button.rect.x + (button.rect.w - size) / 2,
                        y: button.rect.y + (button.rect.h - size) / 2,
                        w: size,
                        h: size,
                    });
                }
            } else if (button.label) {
                painter.text(
                    button.label,
                    button.rect.x + button.rect.w / 2,
                    button.rect.y + 4,
                    colors.text,
                    overlayTokens.fonts.base,
                    'center',
                );
            }

            if (button.badgeColor) {
                const dotX = button.rect.x + button.rect.w - 6;
                const dotY = button.rect.y + 6;
                painter.dot(dotX, dotY, 4, button.badgeColor);
            }
        });

        return targets;
    }
}

function resolveButtonColors(
    enabled: boolean,
    active: boolean,
): { fill: string; stroke: string; text: string } {
    if (!enabled) {
        return {
            fill: overlayTokens.colors.buttonDisabledFill,
            stroke: overlayTokens.colors.buttonDisabledStroke,
            text: '#9a9a9a',
        };
    }
    if (active) {
        return {
            fill: overlayTokens.colors.buttonActiveFill,
            stroke: overlayTokens.colors.buttonActiveStroke,
            text: overlayTokens.colors.textActive,
        };
    }
    return {
        fill: overlayTokens.colors.buttonInactiveFill,
        stroke: overlayTokens.colors.buttonInactiveStroke,
        text: overlayTokens.colors.text,
    };
}
